import {languageMap} from '@github-ui/copilot-chat-helpers/language-info'

// Create a Set of supported file extensions from the language info allowlist
const SUPPORTED_FILE_EXTENSIONS = new Set(Object.values(languageMap).flatMap(lang => lang.extensions ?? []))

// File extensions to exclude from Copilot Spaces even if they're in the allowlist
// Based on the backend denylist from:
// https://github.com/github/github/blob/db70b524abb9946dbbbec6042724f72215ad96c6/app/controllers/copilot/copilot_spaces_controller.rb#L21
// DENIED_EXTENSIONS = %w[.png .jpg .jpeg .gif .bmp .svg .webp .ico .tiff .tif].freeze
const DENIED_FILE_EXTENSIONS = new Set([
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.bmp',
  '.svg',
  '.webp',
  '.ico',
  '.tiff',
  '.tif',
])

/**
 * Checks if a file path has a supported extension for Copilot Spaces.
 * Uses an allowlist approach based on GitHub Linguist data (1400+ file types).
 * Files without extensions are always supported.
 * Directories are always considered supported.
 *
 * @param filePath - The path of the file to check
 * @param isDirectory - Whether the path represents a directory
 * @returns true if the file is supported or is a directory, false otherwise
 */
export function isSupportedFileType(filePath: string, isDirectory: boolean): boolean {
  // Directories are always supported
  if (isDirectory) {
    return true
  }

  // Get the file extension
  const lastDotIndex = filePath.lastIndexOf('.')
  const lastSlashIndex = filePath.lastIndexOf('/')

  // Files without extensions are supported (e.g., README, Makefile)
  if (lastDotIndex === -1 || lastDotIndex < lastSlashIndex) {
    return true
  }

  // Check if the extension is in the allowlist AND not in the denylist (case-insensitive)
  const extension = filePath.substring(lastDotIndex).toLowerCase()
  return SUPPORTED_FILE_EXTENSIONS.has(extension) && !DENIED_FILE_EXTENSIONS.has(extension)
}

/**
 * Filters a list of file paths to only include supported file types.
 *
 * @param filePaths - Array of file paths to filter
 * @param allDirectories - Set of paths that are directories
 * @returns Object containing accepted and rejected file paths
 */
export function filterSupportedFiles(
  filePaths: string[],
  allDirectories: Set<string>,
): {accepted: string[]; rejected: string[]} {
  const accepted: string[] = []
  const rejected: string[] = []

  for (const path of filePaths) {
    const isDirectory = allDirectories.has(path)
    if (isSupportedFileType(path, isDirectory)) {
      accepted.push(path)
    } else {
      rejected.push(path)
    }
  }

  return {accepted, rejected}
}
